import { fail, pass } from "@/core/check";
import type { Lesson } from "@/core/types";
import { blaetterMitSeitenanpassung, blaetterMitWiederholungszeile, readLayout } from "@/office/excel/layout";
import { nichtPruefbar } from "../schnittstelle";

export const lektionDrucken: Lesson = {
  id: "drucken",
  title: "Drucken, ohne 47 Seiten zu produzieren",
  summary: "Eine Tabelle ist endlos, ein Blatt Papier nicht. Drei Einstellungen lösen das Problem.",
  durationMinutes: 11,
  learningGoals: [
    "Die Ausgabe auf eine Seitenbreite anpassen",
    "Die Kopfzeile auf jeder Seite wiederholen",
    "Umbrüche gezielt setzen statt sie hinzunehmen",
  ],
  steps: [
    {
      id: "umbruchvorschau",
      title: "Erst schauen, dann drucken",
      explanation: [
        "Der klassische Excel-Ausdruck: 47 Seiten, auf der letzten steht eine einzelne " +
          "Spalte, und die Kopfzeile gibt es nur auf Seite 1. Das liegt nicht an " +
          "Excel, sondern daran, dass niemand vorher hingeschaut hat.",
        "Strg + P zeigt die Seitenansicht mitsamt der Seitenzahl unten. Das ist der " +
          "erste Blick. Der zweite gehört der Umbruchvorschau unter „Ansicht“ → " +
          "„Umbruchvorschau“: Sie zeigt das Blatt mit blauen Linien, die die " +
          "Seitengrenzen markieren, und – als Wasserzeichen – die Seitennummern. Man " +
          "sieht auf einen Blick, wo etwas abreißt.",
        "In dieser Ansicht lassen sich die blauen Linien mit der Maus verschieben. " +
          "Zieht man eine nach rechts, staucht Excel den Inhalt entsprechend. Das ist " +
          "der schnellste Weg zu einem vernünftigen Umbruch – deutlich schneller, als " +
          "im Seitenlayout-Dialog Prozentwerte zu raten.",
        "Wieder zurück geht es über „Ansicht“ → „Normal“. Die blauen Linien " +
          "verschwinden dann, aber gestrichelte bleiben stehen: Sie zeigen die " +
          "Seitengrenzen auch in der normalen Ansicht an. Wen das stört, der schließt " +
          "die Datei einmal und öffnet sie neu.",
      ],
      keyPoints: [
        "Strg + P zeigt Seitenansicht und Seitenzahl.",
        "Ansicht → Umbruchvorschau zeigt die Seitengrenzen zum Verschieben.",
        "Die gestrichelten Linien danach verschwinden erst beim erneuten Öffnen.",
      ],
    },
    {
      id: "auf-eine-seite",
      title: "Auf eine Seitenbreite",
      explanation: [
        "Die wichtigste Druckeinstellung überhaupt: „Alle Spalten auf einer Seite " +
          "darstellen“. Sie steht im Druckdialog unter Strg + P ganz unten im " +
          "Klappfeld für die Skalierung. Damit hört die Tabelle auf, seitlich " +
          "auszufransen – und genau das Ausfransen erzeugt die meisten überflüssigen " +
          "Seiten.",
        "Der Unterschied zu „Blatt auf einer Seite darstellen“ ist wichtig. Diese " +
          "Einstellung quetscht auch die Höhe zusammen, sodass eine Liste mit " +
          "dreihundert Zeilen auf ein Blatt gestaucht wird – unlesbar klein. Die " +
          "Breite zu begrenzen und die Höhe laufen zu lassen, ist fast immer richtig: " +
          "Man blättert dann Seite für Seite durch eine lesbare Liste.",
        "Bei sehr breiten Tabellen reicht das Stauchen irgendwann nicht mehr. Dann " +
          "hilft das Querformat unter „Seitenlayout“ → „Ausrichtung“ – oder die " +
          "Erkenntnis, dass eine Tabelle mit dreißig Spalten vielleicht gar nicht " +
          "gedruckt werden sollte.",
        "Die Ränder lassen sich im Druckdialog ebenfalls verkleinern. „Schmal“ bringt " +
          "spürbar Platz. Und „Horizontal zentrieren“ unter den Randeinstellungen " +
          "sorgt dafür, dass eine schmale Tabelle nicht linksbündig auf einem großen " +
          "leeren Blatt klebt.",
      ],
      keyPoints: [
        "Strg + P → Skalierung → „Alle Spalten auf einer Seite darstellen“.",
        "Nicht „Blatt auf einer Seite“ – das staucht auch die Höhe unlesbar.",
        "Seitenlayout → Ausrichtung → Querformat für breite Tabellen.",
      ],
      task: {
        instruction:
          "Stelle für dein Datenblatt ein, dass alle Spalten auf eine Seitenbreite " +
          "passen.",
        hint: "Strg + P, unten im Klappfeld für die Skalierung „Alle Spalten auf einer Seite darstellen“ wählen.",
        check: async () => {
          const layout = await readLayout();

          if (layout.pageLayouts === null) {
            return nichtPruefbar(
              "Die Seiteneinrichtung eines Blattes",
              "1.9",
              "Drücke Strg + P und sieh nach, ob die Tabelle in der Vorschau seitlich abreißt.",
            );
          }

          const angepasst = blaetterMitSeitenanpassung(layout);
          if (angepasst.length === 0) {
            return fail(
              `Auf keinem der ${layout.pageLayouts.length} Blätter ist die Breite auf eine Seite ` +
                "begrenzt – die Tabelle franst beim Drucken seitlich aus.",
              "Drücke Strg + P und wähle unten im Klappfeld für die Skalierung " +
                "„Alle Spalten auf einer Seite darstellen“.",
            );
          }

          const erstes = angepasst[0];
          const hoehe =
            erstes.fitToHeight !== null && erstes.fitToHeight > 0
              ? ` Ein Hinweis: Auch die Höhe ist auf ${erstes.fitToHeight} Seite${erstes.fitToHeight === 1 ? "" : "n"} ` +
                "begrenzt. Bei einer längeren Liste wird der Ausdruck dadurch sehr klein – meist " +
                "will man nur die Breite begrenzen."
              : "";
          return pass(
            `Auf „${erstes.sheetName}“ passen alle Spalten auf ${erstes.fitToWidth} Seitenbreite` +
              `${(erstes.fitToWidth ?? 1) === 1 ? "" : "n"}.${hoehe}`,
          );
        },
      },
    },
    {
      id: "wiederholungszeilen",
      title: "Die Kopfzeile auf jeder Seite",
      explanation: [
        "Ab Seite 2 weiß niemand mehr, welche Spalte was bedeutet. Das ist der " +
          "zweitgrößte Ärger beim Drucken und mit einem Handgriff behoben – der " +
          "allerdings gut versteckt liegt.",
        "Zu finden ist er unter „Seitenlayout“ → „Drucktitel“. Im Feld „Wiederholungs" +
          "zeilen oben“ trägt man die Kopfzeile ein, meist $1:$1. Am einfachsten geht " +
          "das, indem man auf das kleine Symbol rechts im Feld klickt und dann den " +
          "Zeilenkopf der Zeile 1 anklickt.",
        "Wichtig ist der Unterschied zum Fixieren aus Modul 1: Das Fixieren wirkt nur " +
          "am Bildschirm, die Wiederholungszeile nur beim Drucken. Beides ist " +
          "unabhängig voneinander und will einzeln eingestellt werden – ein " +
          "fixiertes Fenster wiederholt beim Drucken gar nichts.",
        "Für breite Tabellen, die über mehrere Seiten in die Breite gehen, gibt es " +
          "im selben Dialog „Wiederholungsspalten links“. Damit steht die " +
          "Bezeichnungsspalte auch auf Seite 2 und 3 – sonst sind das Zahlenkolonnen " +
          "ohne Bezug.",
      ],
      keyPoints: [
        "Seitenlayout → Drucktitel → Wiederholungszeilen oben: $1:$1.",
        "Fixieren wirkt am Bildschirm, Wiederholungszeilen beim Drucken.",
        "„Wiederholungsspalten links“ für Tabellen, die in die Breite gehen.",
      ],
      task: {
        instruction:
          "Sorge dafür, dass die Kopfzeile deines Datenblatts auf jeder gedruckten " +
          "Seite wiederholt wird.",
        hint: "Seitenlayout → Drucktitel → im Feld „Wiederholungszeilen oben“ die Zeile 1 anklicken.",
        check: async () => {
          const layout = await readLayout();

          if (layout.pageLayouts === null) {
            return nichtPruefbar(
              "Die Wiederholungszeilen eines Blattes",
              "1.9",
              "Drücke Strg + P und blättere in der Vorschau auf Seite 2 – steht die Kopfzeile dort?",
              true,
            );
          }

          const mitWiederholung = blaetterMitWiederholungszeile(layout);
          if (mitWiederholung.length === 0) {
            return fail(
              "Auf keinem Blatt ist eine Wiederholungszeile eingestellt – ab Seite 2 fehlen die " +
                "Spaltenüberschriften.",
              "Seitenlayout → Drucktitel. Im Feld „Wiederholungszeilen oben“ auf das Symbol rechts " +
                "klicken und dann den Zeilenkopf der Zeile 1 anklicken.",
            );
          }

          const erstes = mitWiederholung[0];
          return pass(
            `Auf „${erstes.sheetName}“ wird ${erstes.printTitleRows} auf jeder Seite wiederholt` +
              (erstes.printTitleColumns !== null
                ? `, dazu die Spalten ${erstes.printTitleColumns}`
                : "") +
              ".",
          );
        },
      },
    },
  ],
};
